import { Element, type PinSlot } from './element';
import type { MnaMatrix } from './mna';

/**
 * 디지털 로직 소자 공통.
 * - 전원핀은 생략(접지 기준 VDD 로 동작)
 * - 입력: 10MΩ (거의 전류 안 먹음), 0.4·VDD / 0.6·VDD 히스테리시스로 0/1 판정
 * - 출력: 50Ω 푸시풀 (1 이면 VDD, 0 이면 0V)
 * - 논리 계산은 스텝이 확정된 뒤(afterAccept)에 하고, 다음 스텝부터 출력에 반영 → 게이트 지연 ≈ 1 스텝
 */
const G_OUT = 1 / 50;
const G_IN = 1e-7;

export abstract class LogicElement extends Element {
  /** 핀별 입력(true)/출력(false) */
  protected abstract ioMap(): boolean[];
  /** 입력 비트 → 출력 비트 */
  protected abstract evaluate(inputs: boolean[]): boolean[];

  protected pinV: number[] = [];
  inLevels: boolean[] = [];
  outLevels: boolean[] = [];

  get vdd(): number {
    return Math.max(this.num('vdd', 5), 0.5);
  }
  override get usesImplicitGround(): boolean {
    return true;
  }
  override get usesLayout(): boolean {
    return this.pinCount > 1;
  }
  override get maxStep(): number {
    return 1e-3;
  }
  override resetState(): void {
    const io = this.ioMap();
    this.pinV = io.map(() => 0);
    this.inLevels = io.filter((x) => x).map(() => false);
    this.outLevels = this.evaluate(this.inLevels);
  }

  override stamp(m: MnaMatrix): void {
    const io = this.ioMap();
    let o = 0;
    io.forEach((isInput, i) => {
      const n = this.nodes[i];
      if (isInput) {
        m.conductance(n, -1, G_IN);
      } else {
        m.conductance(n, -1, G_OUT);
        if (this.outLevels[o]) m.currentSource(-1, n, G_OUT * this.vdd);
        o++;
      }
    });
  }

  override endStep(m: MnaMatrix): void {
    this.pinV = this.nodes.map((n) => m.nodeVoltage(n));
    const out = this.ioMap().findIndex((x) => !x);
    this.voltageDrop = out >= 0 ? this.pinV[out] : this.pinV[0];
    this.current = 0;
  }

  override pinCurrents(): number[] {
    const io = this.ioMap();
    let o = 0;
    return io.map((isInput, i) => {
      const v = this.pinV[i] ?? 0;
      if (isInput) return G_IN * v;
      const lvl = this.outLevels[o++] ? this.vdd : 0;
      return G_OUT * (v - lvl);
    });
  }

  /** 입력 읽기 (히스테리시스) → 논리 계산 */
  override afterAccept(): void {
    const io = this.ioMap();
    const vdd = this.vdd;
    let k = 0;
    io.forEach((isInput, i) => {
      if (!isInput) return;
      const v = this.pinV[i] ?? 0;
      if (v > 0.6 * vdd) this.inLevels[k] = true;
      else if (v < 0.4 * vdd) this.inLevels[k] = false;
      k++;
    });
    this.outLevels = this.evaluate(this.inLevels);
  }

  protected bits(list: boolean[]): string {
    return list.map((b) => (b ? '1' : '0')).join(' ');
  }
}

/* ─────────── 게이트 ─────────── */

export type GateKind = 'and' | 'or' | 'not' | 'nand' | 'nor' | 'xor';
const GATE_NAMES: Record<GateKind, string> = {
  and: 'AND', or: 'OR', not: 'NOT', nand: 'NAND', nor: 'NOR', xor: 'XOR',
};

export class LogicGate extends LogicElement {
  get kind(): GateKind {
    return this.type as GateKind;
  }
  override get pinCount(): number {
    return this.kind === 'not' ? 2 : 3;
  }
  override pinLayout(): PinSlot[] {
    if (this.kind === 'not') {
      return [
        { t: 0, s: 0, label: 'A' },
        { t: 'end', s: 0, label: 'Y' },
      ];
    }
    return [
      { t: 0, s: -1, label: 'A' },
      { t: 0, s: 1, label: 'B' },
      { t: 'end', s: 0, label: 'Y' },
    ];
  }
  protected ioMap(): boolean[] {
    return this.kind === 'not' ? [true, false] : [true, true, false];
  }
  protected evaluate([a, b]: boolean[]): boolean[] {
    switch (this.kind) {
      case 'and': return [a && b];
      case 'or': return [a || b];
      case 'not': return [!a];
      case 'nand': return [!(a && b)];
      case 'nor': return [!(a || b)];
      case 'xor': return [a !== b];
    }
  }
  info(): string[] {
    return [
      `${GATE_NAMES[this.kind]} 게이트`,
      `입력 ${this.bits(this.inLevels)}`,
      `출력 ${this.bits(this.outLevels)}`,
      `VDD ${this.vdd} V`,
    ];
  }
}

/* ─────────── D 플립플롭 ─────────── */

export class DFlipFlop extends LogicElement {
  private q = false;
  private prevClk = false;

  override get pinCount(): number {
    return 4;
  }
  override pinLayout(): PinSlot[] {
    return [
      { t: 0, s: -1, label: 'D' },
      { t: 0, s: 1, label: 'CLK' },
      { t: 'end', s: -1, label: 'Q' },
      { t: 'end', s: 1, label: 'Q̄' },
    ];
  }
  protected ioMap(): boolean[] {
    return [true, true, false, false];
  }
  override resetState(): void {
    this.q = false;
    this.prevClk = false;
    super.resetState();
  }
  protected evaluate([d, clk]: boolean[]): boolean[] {
    if (clk && !this.prevClk) this.q = d; // 상승 에지에서만 D 를 기억
    this.prevClk = clk;
    return [this.q, !this.q];
  }
  info(): string[] {
    return [
      'D 플립플롭',
      `D ${this.inLevels[0] ? 1 : 0} · CLK ${this.inLevels[1] ? 1 : 0}`,
      `Q ${this.q ? 1 : 0} · Q̄ ${this.q ? 0 : 1}`,
      '클럭이 0→1 될 때 D 값을 기억해요',
    ];
  }
}

/* ─────────── 논리 입력 / 표시 / 클럭 (1핀) ─────────── */

/** 논리 입력 — 클릭해서 0/1 을 바꾸는 신호원 */
export class LogicInput extends LogicElement {
  override get pinCount(): number {
    return 1;
  }
  protected ioMap(): boolean[] {
    return [false];
  }
  protected evaluate(): boolean[] {
    return [this.bool('on', false)];
  }
  override stamp(m: MnaMatrix): void {
    this.outLevels = this.evaluate();
    super.stamp(m);
  }
  toggle(): void {
    this.props.on = !this.bool('on', false);
  }
  info(): string[] {
    return ['논리 입력', `출력 ${this.bool('on', false) ? '1 (High)' : '0 (Low)'}`, '클릭하면 바뀌어요'];
  }
}

/** 논리 표시 — 들어온 신호가 1 이면 불이 켜진다 */
export class LogicOutput extends LogicElement {
  override get pinCount(): number {
    return 1;
  }
  protected ioMap(): boolean[] {
    return [true];
  }
  protected evaluate(): boolean[] {
    return [];
  }
  get lit(): boolean {
    return !!this.inLevels[0];
  }
  info(): string[] {
    return ['논리 표시', `입력 ${this.lit ? '1 (High)' : '0 (Low)'}`, `전압 ${(this.pinV[0] ?? 0).toFixed(2)} V`];
  }
}

/** 클럭 — 일정한 주기로 0/1 을 반복 */
export class ClockSource extends LogicElement {
  private t = 0;
  override get pinCount(): number {
    return 1;
  }
  private freq(): number {
    return Math.max(this.num('frequency', 1), 1e-6);
  }
  override get maxStep(): number {
    return Math.min(1e-3, 1 / (this.freq() * 20));
  }
  protected ioMap(): boolean[] {
    return [false];
  }
  protected evaluate(): boolean[] {
    return [Math.floor(this.t * this.freq() * 2) % 2 === 1];
  }
  override resetState(): void {
    this.t = 0;
    super.resetState();
  }
  override beginStep(dt: number, time: number): void {
    this.t = time + dt;
  }
  override stamp(m: MnaMatrix): void {
    this.outLevels = this.evaluate();
    super.stamp(m);
  }
  info(): string[] {
    return ['클럭', `주파수 ${this.freq()} Hz`, `지금 ${this.outLevels[0] ? 1 : 0}`];
  }
}
